<script setup>
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineProps({
  modelValue: { type: String, required: true },
  title: { type: String, required: true },
  description: { type: String, required: true },
  icon: { type: String, required: true },
  schemes: { type: Array, required: true },
  optionsLabel: { type: String, required: true },
});

const emit = defineEmits(['update:modelValue']);
</script>

<template>
  <section class="rounded-2xl border border-n-weak bg-n-solid-2 p-5">
    <div
      class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="flex min-w-0 items-center gap-3">
        <span
          class="grid size-10 shrink-0 place-content-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <Icon :icon="icon" class="size-5" />
        </span>
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">{{ title }}</h3>
          <p class="text-xs text-n-slate-10">{{ description }}</p>
        </div>
      </div>
      <span
        class="inline-flex w-fit items-center rounded-full bg-n-alpha-2 px-2.5 py-1 text-[11px] font-semibold text-n-slate-10"
      >
        {{ schemes.length }} {{ optionsLabel }}
      </span>
    </div>

    <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      <button
        v-for="scheme in schemes"
        :key="scheme.key"
        type="button"
        class="group relative overflow-hidden rounded-2xl border bg-n-solid-1 p-2.5 text-left transition-all duration-200"
        :class="
          modelValue === scheme.key
            ? 'border-n-brand ring-2 ring-n-brand/15'
            : 'border-n-weak hover:-translate-y-0.5 hover:border-n-slate-7 hover:shadow-md'
        "
        :aria-pressed="modelValue === scheme.key"
        @click="emit('update:modelValue', scheme.key)"
      >
        <div
          class="h-20 w-full overflow-hidden rounded-xl shadow-inner"
          :style="{
            backgroundColor: scheme.bg,
            border: `1px solid ${scheme.ink}14`,
          }"
        >
          <div
            class="flex h-4 items-center gap-1 px-2"
            :style="{
              backgroundColor: scheme.surface,
              borderBottom: `1px solid ${scheme.ink}12`,
            }"
          >
            <span
              class="size-1 rounded-full opacity-35"
              :style="{ backgroundColor: scheme.ink }"
            />
            <span
              class="size-1 rounded-full opacity-20"
              :style="{ backgroundColor: scheme.ink }"
            />
            <span
              class="size-1 rounded-full opacity-10"
              :style="{ backgroundColor: scheme.ink }"
            />
          </div>
          <div class="flex h-16">
            <div
              class="flex w-1/4 shrink-0 flex-col gap-1.5 p-1.5"
              :style="{
                backgroundColor: scheme.sidebar,
                borderRight: `1px solid ${scheme.ink}12`,
              }"
            >
              <span
                class="size-2 rounded-full opacity-25"
                :style="{ backgroundColor: scheme.ink }"
              />
              <span
                class="h-1 w-full rounded-full opacity-15"
                :style="{ backgroundColor: scheme.ink }"
              />
              <span
                class="h-1 w-3/4 rounded-full opacity-10"
                :style="{ backgroundColor: scheme.ink }"
              />
              <span
                class="mt-auto h-2.5 w-full rounded-sm"
                :style="{ backgroundColor: scheme.accent }"
              />
            </div>
            <div class="flex flex-1 flex-col gap-1.5 p-1.5">
              <div
                class="flex h-5 items-center gap-1.5 rounded-md px-1.5"
                :style="{ backgroundColor: scheme.accent }"
              >
                <span
                  class="size-2 rounded-full opacity-25"
                  :style="{ backgroundColor: scheme.ink }"
                />
                <span
                  class="h-1 w-1/2 rounded-full opacity-30"
                  :style="{ backgroundColor: scheme.ink }"
                />
              </div>
              <div
                class="flex-1 rounded-md p-1.5"
                :style="{
                  backgroundColor: scheme.surface,
                  border: `1px solid ${scheme.ink}12`,
                }"
              >
                <span
                  class="block h-1 w-4/5 rounded-full opacity-25"
                  :style="{ backgroundColor: scheme.ink }"
                />
                <span
                  class="mt-1 block h-1 w-1/2 rounded-full opacity-15"
                  :style="{ backgroundColor: scheme.ink }"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="mt-2.5 flex items-start justify-between gap-2 px-0.5">
          <span class="min-w-0">
            <span class="block truncate text-xs font-semibold text-n-slate-12">
              {{ scheme.label }}
            </span>
            <span class="block truncate text-[10px] text-n-slate-9">
              {{ scheme.description }}
            </span>
          </span>
          <span class="mt-0.5 flex shrink-0 items-center gap-0.5">
            <span
              v-for="color in [scheme.sidebar, scheme.surface, scheme.accent]"
              :key="color"
              class="size-2 rounded-full ring-1 ring-n-weak"
              :style="{ backgroundColor: color }"
            />
          </span>
        </div>

        <span
          v-if="modelValue === scheme.key"
          class="absolute right-4 top-4 grid size-5 place-content-center rounded-full bg-n-brand text-white shadow-sm ring-2 ring-white/20"
        >
          <Icon icon="i-lucide-check" class="size-3.5 stroke-[2.5]" />
        </span>
      </button>
    </div>
  </section>
</template>
